import { ChevronRightIcon } from "@heroicons/react/24/outline";
import CallbackModal from "@/components/callback-modal";
import Image from "@/components/image";
import { useSession } from "@/components/providers/session";
import { format } from "date-fns";
import { useState } from "react";
import { currency } from "@/utils/formats";
import MD from "react-markdown";
import Link from "next/link";

export default function Video({
  fields,
  first = false,
  sys,
  includes,
  primary,
  event,
  events,
}) {
  const session = useSession();
  const titleParts = fields.title && fields.title.split(" ");
  const last = titleParts.pop();
  const Title = ({ children }) => {
    return first ? <h1>{children}</h1> : <h3>{children}</h3>;
  };
  const [state, setState] = useState({
    faqs: false,
    courses: false,
    showCallbackModal: false,
  });

  const nextCourse =
    event &&
    event.events &&
    event.events.length > 0 &&
    event.events.find((x) => x.registrations.length < x.seats);

  const startDate =
    nextCourse && nextCourse.settings && nextCourse.settings.dates
      ? new Date(nextCourse.settings.dates[0].start)
      : nextCourse && nextCourse.start
      ? new Date(nextCourse.start)
      : null;

  const over =
    fields.title ===
    "Elevate Your Expertise And Grow Your Career In Aesthetics";

  const level3 =
    primary && primary.sys && primary.sys.id === "1ETJJG5eM8sxMLAzv3eWay";

  return (
    <div className="relative bg-white overflow-hidden">
      <div
        className="hidden lg:block lg:absolute lg:inset-0"
        aria-hidden="true"
      >
        <svg
          className="absolute top-0 left-1/2 transform translate-x-64 -translate-y-8"
          width="640"
          height="784"
          fill="none"
          viewBox="0 0 640 784"
        >
          <defs>
            <pattern
              id="9ebea6f4-a1f5-4d96-8c4e-4c2abf658047"
              x="118"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <rect
                x="0"
                y="0"
                width="4"
                height="4"
                className="text-gray-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            y="72"
            width="640"
            height="640"
            className="text-gray-50"
            fill="currentColor"
          />
          <rect
            x="118"
            width="404"
            height="784"
            fill="url(#9ebea6f4-a1f5-4d96-8c4e-4c2abf658047)"
          />
        </svg>
      </div>
      <section className="mt-8 mb-12 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 lg:mt-16 lg:mb-16 relative z-10">
        <div className="lg:grid lg:grid-cols-10 lg:gap-24">
          <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-5 lg:text-left flex flex-col justify-center">
            {nextCourse ? (
              <div className="flex mb-2 md:mx-auto lg:mx-0">
                <div className="relative flex items-center gap-x-4 rounded-full px-4 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-brand-100 transition-all duration-200 hover:shadow-lg hover:shadow-brand-200">
                  <span className="font-semibold text-brand-600">
                    {currency(nextCourse.seat_price)}
                  </span>
                  <span
                    className="h-4 w-px bg-gray-900/10"
                    aria-hidden="true"
                  />
                  <a
                    href="#course-dates"
                    className="flex items-center gap-x-1 glow"
                  >
                    <span className="absolute inset-0" aria-hidden="true" />
                    Next course: {format(startDate, "do MMMM yyyy")}
                    <ChevronRightIcon
                      className="-mr-2 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </a>
                </div>
              </div>
            ) : null}
            {fields.logo ? (
              <div className="md:mx-auto lg:mx-0">
                <img
                  src={fields.logo.fields.file.url}
                  className="h-8 w-auto mt-4"
                  alt=""
                />
              </div>
            ) : null}
            {fields.title && (
              <Title>
                <span className="mt-1 block text-2xl md:text-4xl tracking-tight font-extrabold sm:text-5xl xl:text-6xl">
                  <span className="inline text-gray-900">
                    {titleParts.join(" ")}
                  </span>
                  {last && (
                    <span className="inline text-brand-600"> {last}</span>
                  )}
                </span>
              </Title>
            )}
            {level3 ? (
              <div className="relative block md:hidden mt-4 md:mt-0 h-full w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 aspect-w-16 aspect-h-9">
                <iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/BDJZEfP5kkI?si=T_LOH70lj0IItTcU"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            ) : over ? (
              <div className="relative block md:hidden mt-4 md:mt-0 h-full w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 aspect-w-16 aspect-h-9">
                <iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/nOn5ivzuhPg?si=93Ivnib7EGYBBSi0"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            ) : (
              <button
                type="button"
                className={`relative block md:hidden mt-4 md:mt-0 h-full w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 ${
                  !fields.video && fields.image ? "aspect-w-16 aspect-h-9" : ""
                }`}
              >
                <span className="sr-only">Watch our video to learn more</span>
                {!fields.video && fields.image && (
                  <Image
                    alt=""
                    fill
                    fields={fields.image}
                    className="w-full h-full object-cover object-center"
                  />
                )}
                {fields.video && (
                  <div className="relative text-base w-full mx-auto max-w-prose lg:max-w-none rounded-lg shadow-lg overflow-hidden">
                    <figure>
                      <div className="aspect-w-12 aspect-h-8">
                        <video
                          src={fields.video.fields.file.url}
                          autoPlay
                          playsInline
                          muted
                          loop
                          controls
                          controlsList="nodownload nofullscreen noremoteplayback"
                          onContextMenu={() => false}
                          className="object-cover object-center"
                        ></video>
                      </div>
                      <figcaption className="sr-only">
                        <span className="">{fields.video.fields.title}</span>
                      </figcaption>
                    </figure>
                  </div>
                )}
              </button>
            )}
            {fields.text && (
              <div className="pt-4 prose">
                <MD
                  components={{
                    strong: (props) => {
                      return <strong {...props} className="text-brand-600" />;
                    },
                  }}
                >
                  {fields.text}
                </MD>
              </div>
            )}
            {primary &&
              primary.fields.type &&
              primary.fields.type === "Course" && (
                <>
                  <div className="mt-6 flex flex-col md:flex-row items-center gap-y-4 md:gap-x-6 md:mx-auto lg:mx-0">
                    <button
                      type="button"
                      onClick={() => {
                        session.addCourse({
                          ...primary,
                          event,
                          package: event && event.package,
                          selectedEvent: nextCourse
                            ? nextCourse
                            : events && events.length > 0 && events[0]
                            ? events[0]
                            : null,
                        });
                        setTimeout(() => {
                          window.location.href = "/checkout";
                        }, 100);
                      }}
                      className="w-full md:w-max rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500"
                    >
                      <span className="relative z-[1]">Book Your Training</span>
                    </button>
                    <Link
                      href="/request-a-callback"
                      className="w-full md:w-max group rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-brand-100 transition-all duration-200 hover:shadow-lg hover:shadow-brand-200 text-center"
                    >
                      Speak With An Advisor{" "}
                      <span
                        aria-hidden="true"
                        className="group-hover:pl-1 transition-all duration-150"
                      >
                        →
                      </span>
                    </Link>
                    <a
                      href="https://calendar.app.google/DNfJkzqESkA3NSRM7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full md:w-max group rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-brand-100 transition-all duration-200 hover:shadow-lg hover:shadow-brand-200 text-center"
                    >
                      <span className="relative z-[1] text-center">
                        Book A College Tour
                      </span>
                    </a>
                    {/* <button
                      type="button"
                      onClick={() => {
                        setState({ ...state, showCallbackModal: true });
                      }}
                      className="w-full md:w-max group rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-brand-100 transition-all duration-200 hover:shadow-lg hover:shadow-brand-200"
                    >
                      Speak with an advisor{" "}
                      <span
                        aria-hidden="true"
                        className="group-hover:pl-1 transition-all duration-150"
                      >
                        →
                      </span>
                    </button> */}
                  </div>
                </>
              )}
          </div>
          <div className="hidden md:block mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-5 lg:flex lg:items-center">
            <svg
              className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-8 scale-75 origin-top sm:scale-100 lg:hidden"
              width="640"
              height="784"
              fill="none"
              viewBox="0 0 640 784"
              aria-hidden="true"
            >
              <defs>
                <pattern
                  id="4f4f415c-a0e9-44c2-9601-6ded5a34a13e"
                  x="118"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x="0"
                    y="0"
                    width="4"
                    height="4"
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                y="72"
                width="640"
                height="640"
                className="text-gray-50"
                fill="currentColor"
              />
              <rect
                x="118"
                width="404"
                height="784"
                fill="url(#4f4f415c-a0e9-44c2-9601-6ded5a34a13e)"
              />
            </svg>
            <div className="relative w-full rounded-lg shadow-lg">
              {level3 ? (
                <div className="relative block mt-4 md:mt-0 h-full w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 aspect-w-16 aspect-h-9">
                  <iframe
                    src="https://www.youtube.com/embed/BDJZEfP5kkI?si=T_LOH70lj0IItTcU"
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : over ? (
                <div className="relative block mt-4 md:mt-0 h-full w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 aspect-w-16 aspect-h-9">
                  <iframe
                    src="https://www.youtube.com/embed/nOn5ivzuhPg?si=93Ivnib7EGYBBSi0"
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className={`relative block h-full w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 ${
                    !fields.video && fields.image ? "min-h-[450px]" : ""
                  }`}
                >
                  <span className="sr-only">Watch our video to learn more</span>
                  {!fields.video && fields.image && (
                    <Image
                      alt=""
                      fill
                      fields={fields.image}
                      className="w-full h-full object-cover object-center"
                    />
                  )}
                  {fields.video && (
                    <div className="relative text-base w-full mx-auto max-w-prose lg:max-w-none rounded-lg shadow-lg overflow-hidden">
                      <figure>
                        <div className="aspect-w-12 aspect-h-8 ">
                          <video
                            src={fields.video.fields.file.url}
                            autoPlay
                            playsInline
                            muted
                            loop
                            controls
                            controlsList="nodownload nofullscreen noremoteplayback"
                            onContextMenu={() => false}
                            className="object-cover object-center"
                          ></video>
                        </div>
                        <figcaption className="sr-only">
                          <span className="">{fields.video.fields.title}</span>
                        </figcaption>
                      </figure>
                    </div>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      {state.showCallbackModal && (
        <CallbackModal
          event={event}
          showCallbackModal={state.showCallbackModal}
          setShow={setState}
        />
      )}
    </div>
  );
}
